import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useOutlets } from '@/hooks/useOutlets';
import { CashierForm } from '@/components/cashiers/CashierForm';
import { OutletAccessManager } from '@/components/cashiers/OutletAccessManager';
import { AuditLogViewer } from '@/components/cashiers/AuditLogViewer';
import type { CashierUser, UpdateCashierRequest } from '@photobooth/api-client';

export function EditCashierPage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { outlets } = useOutlets();
  const [cashier, setCashier] = useState<CashierUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCashier = async () => {
      if (!userId) {
        setError('Cashier ID is required');
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.cashiers.getById(userId);
        if (response.success && response.data) {
          // API may wrap the cashier in a { success, data } envelope – unwrap if needed
          const raw = response.data as any;
          const cashierData = raw && raw.data ? raw.data : raw;
          setCashier(cashierData);
        } else {
          setError(response.error?.message || 'Failed to load cashier');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load cashier');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCashier();
  }, [userId]);

  const handleUpdate = async (data: UpdateCashierRequest) => {
    if (!userId || !cashier) {
      throw new Error('Cashier ID is required');
    }

    // Update cashier details
    const response = await api.cashiers.update(userId, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update cashier');
    }

    // Handle outlet access changes if the form includes outlet data
    // Note: The form doesn't actually change outlets in update mode
    // Outlet access is managed separately in task 19.3
    
    navigate('/cashiers');
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !cashier) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/cashiers')}
            className="text-primary hover:underline mb-4"
          >
            ← Back to Cashiers
          </button>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error || 'Cashier not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/cashiers')}
          className="text-primary hover:underline mb-4"
        >
          ← Back to Cashiers
        </button>

        <div className="space-y-6">
          <div className="bg-surface rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-text mb-6">Edit Cashier</h1>
            <CashierForm
              cashier={cashier}
              outlets={outlets}
              onSubmit={handleUpdate}
              onCancel={() => navigate('/cashiers')}
            />
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
            <OutletAccessManager userId={userId!} availableOutlets={outlets} />
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
            <AuditLogViewer userId={userId!} />
          </div>
        </div>
      </div>
    </div>
  );
}
